/**
 * Safari ITP proxy support for generated aUT tags.
 *
 * Safari truncates the lifetime of cookies set from a CNAME-cloaked third-party
 * host, so domains onboarded with the proxy setup route Safari traffic through
 * the client's own website domain plus a required path prefix instead. Detection
 * has to happen in the browser, so the generated tag carries both hosts and
 * picks one at runtime.
 *
 * Only domain records with `proxyEnabled` in their domain list config are
 * onboarded with this setup. Safari ITP is implied by that proxy support rather
 * than separately switched on, so the tag forms collect the client website domain
 * and path prefix for those domains and clear them for every other domain.
 */

/**
 * Safari/WebKit detection, emitted verbatim into the tag snippet (hence a string
 * and not a function). Apple vendor plus "Safari" in the user agent, with the
 * non-Safari browsers that also ship "Safari" in their user agent filtered out.
 *
 * The `\\/` sequences are deliberate: they emit `\/` so the escaped slashes
 * survive into the generated regex literal.
 */
export const SAFARI_DETECTION_EXPR =
    "/apple/i.test(navigator.vendor)&&/safari/i.test(navigator.userAgent)&&"
    + "!/chrome|crios|chromium|android|firefox|fxios|edg\\/|edgios|opr\\/"
    + "|samsungbrowser|duckduckgo|brave|yabrowser|ucbrowser/i.test(navigator.userAgent)";

/**
 * Normalize a user-entered path prefix into exactly one leading slash and no
 * trailing slash, so it concatenates cleanly ahead of the aUT `/1/{lm}/{tt}` path.
 * Returns "" for anything that carries no path.
 */
export function normalizePathPrefix(pathPrefix) {
    const stripped = String(pathPrefix || "").trim().replace(/^\/+/, "").replace(/\/+$/, "");
    return stripped ? `/${stripped}` : "";
}

/**
 * Find a domain record by name. Accepts either the full aUT domain list (an array
 * of edge hostname objects, as on Generate Tag and Tag Template) or a single edge
 * hostname object (as on the client checklist).
 */
export function findDomainRecord(domainList, domainName) {
    if (!domainName) return null;
    const edgeHosts = Array.isArray(domainList) ? domainList : [domainList];
    for (const edgeHost of edgeHosts) {
        const record = (edgeHost && edgeHost.domains && edgeHost.domains.items || [])
            .find((item) => item && item.name === domainName);
        if (record) return record;
    }
    return null;
}

/** Whether the named domain is onboarded with the proxy setup Safari ITP needs. */
export function isProxyEnabledDomain(domainList, domainName) {
    const record = findDomainRecord(domainList, domainName);
    if (!record) return false;
    return record.proxyEnabled === true || record.proxyEnabled === "true";
}

/**
 * Resolve the Safari ITP inputs into the pieces a tag snippet needs.
 *
 * A configured client website domain is the whole gate: the tag forms only collect
 * one for proxy-enabled domains and clear it otherwise, so there is no separate
 * flag to keep in sync. With none, every piece is inert and the caller emits a
 * snippet identical to its pre-Safari-ITP output. Callers should splice all five
 * pieces in; mixing them with hardcoded equivalents is what breaks the inert case.
 *
 * @param {Object} input
 * @param {String} input.autDomain            aUT domain serving non-Safari traffic
 * @param {String} input.clientWebsiteDomain  client's own domain serving Safari traffic
 * @param {String} input.pathPrefix           prefix prepended to the path on that domain
 * @returns {{active: Boolean, detectionStatement: String, cdmnExpr: String,
 *            pathPrefixValueExpr: String, excludeKeys: String, srcHostExpr: String}}
 */
export function safariItpTagParts({ autDomain, clientWebsiteDomain, pathPrefix } = {}) {
    const proxyDomain = String(clientWebsiteDomain || "").trim();

    if (!proxyDomain) {
        return {
            active: false,
            detectionStatement: "",
            cdmnExpr: `'${autDomain}'`,
            pathPrefixValueExpr: "",
            excludeKeys: "cdmn:1,lm:1,tt:1",
            srcHostExpr: "p.cdmn",
        };
    }

    const prefix = normalizePathPrefix(pathPrefix);
    return {
        active: true,
        detectionStatement: `var isSafari=${SAFARI_DETECTION_EXPR};`,
        cdmnExpr: `isSafari?'${proxyDomain}':'${autDomain}'`,
        // Just the value, so each caller can key it in its own snippet's spacing
        // style. pathPrefix is a routing hint for the injector, never a beacon
        // query param, so it also has to join `excludeKeys` below.
        pathPrefixValueExpr: `isSafari?'${prefix}':''`,
        excludeKeys: "cdmn:1,lm:1,tt:1,pathPrefix:1",
        srcHostExpr: "p.cdmn+(p.pathPrefix||'')",
    };
}
